import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col } from "reactstrap";
import SimpleBar from "simplebar-react";
import SockJS from "sockjs-client";
import { Stomp } from "@stomp/stompjs";
import axios from "axios";
import { API } from "../../../api/urls";

// Import Icons
import FeatherIcon from "feather-icons-react";

// i18n
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Eskiden metin kutusunda wordBreak:"break-all" vardı; kelimeleri harf ortasından
// kesiyordu ("Teşekkür e/deriz", "3.200 T/L"). overflow-wrap yalnız satıra sığmayan
// tek parçayı (uzun URL/kod) böler, normal kelimeleri bölmez.
// min-width:0 + "anywhere": flex öğesi en uzun bölünemez parça kadar genişleyip
// dropdown'ı yatayda taşırıyordu; "break-word" min-content genişliğini düşürmüyor.
const NOTIFICATION_STYLES = `
  .notification-item .notification-text {
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: normal;
    font-size: 13px;
    line-height: 1.45;
    color: #212529;
  }
  .notification-item .notification-time {
    font-size: 11.5px;
    color: #98a6ad;
    margin-top: 2px;
  }
  /* Tema (_topbar.scss) .notification-item içindeki her .d-flex'e padding veriyor; metin
     satırı ikonun hizasından aşağı kayıp iki yandan daralıyordu. */
  .notification-item .d-flex.notification-row {
    padding: 0;
  }
  /* Nokta metnin akışından çıkarıldı: h6 içindeyken son kelimeyi alt satıra itiyordu. */
  .notification-item .notification-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #dc3545;
    margin-top: 5px;
  }
  .notification-item + .notification-item {
    border-top: 1px solid #f0f2f5;
  }
  .notification-item:hover {
    background-color: #f6f8fb;
  }
`;

const NotificationDropdown = (props) => {
  const [menu, setMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [visibleNotificationIds, setVisibleNotificationIds] = useState(new Set());
  const [userRoles, setUserRoles] = useState([]);

  const stompClient = useRef(null);
  const isConnected = useRef(false);
  const observerRef = useRef(null);
  const notificationRefs = useRef({});
  const navigate = useNavigate();

  const isNotificationSupported = () => {
    return typeof window !== "undefined" && "Notification" in window;
  };

  const fetchNotifications = async () => {
    try {

      // Bildirimleri çek
      const notificationsResponse = await axios.get(API.notifications);

      if (notificationsResponse.data.statusCode === 200) {
        const notificationData = notificationsResponse.data.data || [];
        setNotifications(notificationData);
      }

      const unreadResponse = await axios.get(API.notifications_unread_count);

      if (unreadResponse.data.statusCode === 200) {
        setUnreadCount(unreadResponse.data.data || 0);
      }

    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Destek bildirimi tıklanınca hangi panele gidileceğini rol belirler
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const response = await axios.get(API.auth_me);
        if (response.data.statusCode === 200) {
          setUserRoles(response.data.data.roles || []);
        }
      } catch (error) {
        // Rol alınamazsa yönlendirme müşteri ekranına düşer; bildirim akışı bozulmaz.
      }
    };

    fetchUserRoles();
  }, []);

  useEffect(() => {

    const connectWebSocket = () => {
      try {
        const socket = new SockJS(API.websocket);
        stompClient.current = Stomp.over(socket);

        stompClient.current.debug = () => {};

        const connectHeaders = {};

        stompClient.current.connect(connectHeaders, (frame) => {
          isConnected.current = true;

          stompClient.current.subscribe(`/user/queue/private-notifications`, (message) => {
            console.log("Yeni bildirim alındı:", message.body);

            try {
              const notification = JSON.parse(message.body);

              setNotifications(prevNotifications => [notification, ...prevNotifications]);
              setUnreadCount(prevCount => prevCount + 1);

              // 🔐 iOS / unsupported tarayıcıları koru
              if (isNotificationSupported() && Notification.permission === "granted") {
                new Notification("Yeni Bildirim", {
                  body: notification.message,
                  icon: "/favicon.ico"
                });
              }
            } catch (error) {
              console.error("WebSocket mesaj işleme hatası:", error);
            }
          });

        }, (error) => {
          console.error("WebSocket bağlantı hatası:", error);
          isConnected.current = false;

          // 5 saniye sonra tekrar bağlanmayı dene
          setTimeout(connectWebSocket, 5000);
        });

      } catch (error) {
        console.error("WebSocket kurulum hatası:", error);
      }
    };

    connectWebSocket();

    // 🔐 İzin isteğini de guard'la
    if (isNotificationSupported() && Notification.permission === "default") {
      Notification.requestPermission().catch((err) => {
        console.error("Notification izin isteği alınamadı:", err);
      });
    }

    // Cleanup function
    return () => {
      if (stompClient.current && isConnected.current) {
        stompClient.current.disconnect();
        console.log("WebSocket bağlantısı kesildi");
      }
    };
  }, []);

  const markVisibleAsRead = useCallback(async () => {
    if (visibleNotificationIds.size === 0) return;

    const unreadVisibleIds = Array.from(visibleNotificationIds).filter(id => {
      const notification = notifications.find(n => n.id === id);
      return notification && !notification.isRead;
    });

    if (unreadVisibleIds.length === 0) return;

    try {

      const response = await axios.put(API.notifications_mark_read_bulk, {
        ids: unreadVisibleIds
      });

      if (response.data.statusCode === 200) {
        console.log(`${unreadVisibleIds.length} bildirim okundu olarak işaretlendi`);

        // Local state'i güncelle
        setNotifications(prevNotifications =>
            prevNotifications.map(n =>
                unreadVisibleIds.includes(n.id) ? { ...n, isRead: true } : n
            )
        );

        setUnreadCount(prevCount => Math.max(0, prevCount - unreadVisibleIds.length));
      }
    } catch (error) {
      console.error("Görünür bildirimler okundu olarak işaretlenemedi:", error);
    }
  }, [visibleNotificationIds, notifications]);

  const handleIntersection = useCallback((entries) => {
    entries.forEach(entry => {
      const notificationId = entry.target.dataset.notificationId;
      if (entry.isIntersecting) {
        setVisibleNotificationIds(prev => new Set([...prev, notificationId]));
      } else {
        setVisibleNotificationIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(notificationId);
          return newSet;
        });
      }
    });
  }, []);

  useEffect(() => {
    if (menu && notifications.length > 0) {
      observerRef.current = new IntersectionObserver(handleIntersection, {
        threshold: 0.5, // Bildirimin %50'si görünür olduğunda tetikle
        rootMargin: '0px'
      });

      // Mevcut notification elementlerini gözlemle
      Object.values(notificationRefs.current).forEach(ref => {
        if (ref) {
          observerRef.current.observe(ref);
        }
      });
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [menu, notifications, handleIntersection]);

  // Görünür bildirimler değiştiğinde okundu işaretle (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (menu && visibleNotificationIds.size > 0) {
        markVisibleAsRead();
      }
    }, 200); // 1 saniye bekle

    return () => clearTimeout(timer);
  }, [visibleNotificationIds, menu, markVisibleAsRead]);

  // Menu kapandığında görünür ID'leri temizle
  useEffect(() => {
    if (!menu) {
      setVisibleNotificationIds(new Set());
    }
  }, [menu]);

  // Bildirimi okundu olarak işaretle (manuel)
  const markAsRead = async (notificationId) => {
    try {
      const response = await axios.put(`${API.notifications_mark_read}/${notificationId}/read`, {});

      if (response.data.statusCode === 200) {
        // Local state'i güncelle
        setNotifications(prevNotifications =>
            prevNotifications.map(n =>
                n.id === notificationId ? { ...n, isRead: true } : n
            )
        );

        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      }
    } catch (error) {
      console.error("Bildirim okundu olarak işaretlenemedi:", error);
    }
  };

  // Zaman formatını düzenle
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Az önce";
    if (diffInMinutes < 60) return `${diffInMinutes} dakika önce`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} saat önce`;
    return `${Math.floor(diffInMinutes / 1440)} gün önce`;
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead && notification.id) {
      await markAsRead(notification.id);
    }

    // Dropdown'u kapat
    setMenu(false);

    // Bildirim türüne göre yönlendir
    const navigationUrl = getNavigationUrl(notification);
    if (navigationUrl) {
      navigate(navigationUrl);
    }
  };

  const isSupportStaff = userRoles.some(r => r.includes("ROLE_SUPER_ADMIN"));

  const getNavigationUrl = (notification) => {
    switch (notification.type) {
      case "PAYMENT_SUCCESS":
        return "/payment-history";
      case "SUPPORT_TICKET":
        // referenceId = talep kimliği; destek personeli yönetim ekranına, kullanıcı kendi ekranına gider.
        if (!notification.referenceId) return "/support";
        return isSupportStaff
            ? `/admin/tickets/${notification.referenceId}`
            : `/support/${notification.referenceId}`;
      case "REFERRAL_REWARD":
        return "/subscription/details";
      default:
        return null;
    }
  };

  // Bildirim türüne göre ikon
  const getNotificationIcon = (type) => {
    switch (type) {
      case "PAYMENT_SUCCESS":
        return <i className="bx bx-badge-check" />;
      case "PAYMENT_FAILED":
        return <i className="bx bx-error-circle" />;
      case "ORDER_PLACED":
        return <i className="bx bx-cart" />;
      case "ORDER_COMPLETED":
        return <i className="bx bx-check-circle" />;
      case "MESSAGE":
        return <i className="bx bx-message" />;
      case "SYSTEM":
        return <i className="bx bx-cog" />;
      case "WARNING":
        return <i className="bx bx-error" />;
      case "INFO":
        return <i className="bx bx-info-circle" />;
      case "ANNOUNCEMENT":
        return <i className="bx bx-broadcast" />;
      default:
        return <i className="bx bx-bell" />;
    }
  };

  // Önem derecesine göre renk (destek talebinde önceliğe karşılık gelir)
  const getSeverityColor = (severity) => {
    switch (severity) {
      case "CRITICAL":
        return "bg-danger";   // Acil
      case "WARNING":
        return "bg-warning";  // Yüksek
      default:
        return null;          // INFO/boş → tip bazlı varsayılana düş
    }
  };

  // Bildirim türüne göre renk
  const getNotificationColor = (type) => {
    switch (type) {
      case "PAYMENT_SUCCESS":
      case "ORDER_COMPLETED":
        return "bg-success";
      case "PAYMENT_FAILED":
      case "WARNING":
        return "bg-warning";
      case "ORDER_PLACED":
        return "bg-primary";
      case "MESSAGE":
      case "INFO":
        return "bg-info";
      case "SYSTEM":
        return "bg-secondary";
      default:
        return "bg-secondary";
    }
  };

  return (
      <React.Fragment>
        <style>{NOTIFICATION_STYLES}</style>
        <Dropdown
            isOpen={menu}
            toggle={() => setMenu(!menu)}
            className="dropdown d-inline-block"
            tag="li"
        >
          <DropdownToggle
              className="btn header-item noti-icon position-relative"
              tag="button"
              id="page-header-notifications-dropdown"
          >
            <FeatherIcon icon="bell" className="icon-lg" />
            {unreadCount > 0 && (
                <span className="badge bg-danger rounded-pill">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
            )}
          </DropdownToggle>

          <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
            <div className="p-3">
              <Row className="align-items-center">
                <Col>
                  <h6 className="m-0">
                    {props.t("Bildirimler")}
                    {unreadCount > 0 && (
                        <span className="badge bg-primary ms-2">{unreadCount}</span>
                    )}
                  </h6>
                </Col>
              </Row>
            </div>

            <SimpleBar style={{ height: "300px" }}>
              {loading ? (
                  <div className="text-center p-3">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="sr-only">Yükleniyor...</span>
                    </div>
                  </div>
              ) : notifications.length === 0 ? (
                  <div className="text-center p-4">
                    <FeatherIcon icon="bell-off" className="text-muted mb-2" size={32} />
                    <p className="text-muted mb-0">Henüz bildirim yok</p>
                  </div>
              ) : (
                  notifications.map((notification, index) => (
                      <div
                          key={notification.id || index}
                          ref={el => {
                            if (notification.id) {
                              notificationRefs.current[notification.id] = el;
                            }
                          }}
                          data-notification-id={notification.id}
                          className={`text-reset notification-item d-block ${!notification.isRead ? 'bg-light' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.preventDefault();
                            handleNotificationClick(notification);
                          }}
                      >
                        <div className="d-flex p-3">
                          <div className="avatar-sm me-3 flex-shrink-0">
                          <span className={`avatar-title ${getSeverityColor(notification.severity) || getNotificationColor(notification.type)} rounded-circle font-size-16`}>
                            {getNotificationIcon(notification.type)}
                          </span>
                          </div>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-start gap-2 notification-row">
                              <div className="notification-text flex-grow-1">
                                {notification.title && <strong>{notification.title}: </strong>}
                                {notification.message}
                              </div>
                              {!notification.isRead && (
                                  <span className="notification-dot flex-shrink-0" />
                              )}
                            </div>
                            <div className="notification-time">
                              <i className="mdi mdi-clock-outline" />
                              {" "}
                              {formatTime(notification.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                  ))
              )}
            </SimpleBar>

          </DropdownMenu>
        </Dropdown>
      </React.Fragment>
  );
};

export default withTranslation()(NotificationDropdown);

NotificationDropdown.propTypes = {
  t: PropTypes.func.isRequired,
};